import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getExpiryDashboard, getExpiryList, getExpiryCalendar, 
  createOrderFromExpiry, sendExpiryReminders, getServices
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Calendar, AlertTriangle, Clock, FileWarning, Building2, User,
  ChevronLeft, ChevronRight, Bell, Plus, Send, RefreshCw,
  AlertCircle, CheckCircle, XCircle, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatDate } from '@/lib/utils';

const ALERT_LEVELS = [
  { value: 'expired', label: 'Expired', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-100' },
  { value: 'emergency', label: 'Emergency (3 days)', color: 'bg-red-400', textColor: 'text-red-600', bgColor: 'bg-red-50' },
  { value: 'critical', label: 'Critical (7 days)', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-100' },
  { value: 'urgent', label: 'Urgent (15 days)', color: 'bg-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-100' },
  { value: 'action_required', label: 'Action Required (30 days)', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  { value: 'planning', label: 'Planning (60 days)', color: 'bg-blue-400', textColor: 'text-blue-700', bgColor: 'bg-blue-100' },
  { value: 'early_warning', label: 'Early Warning (90 days)', color: 'bg-green-400', textColor: 'text-green-700', bgColor: 'bg-green-100' },
];

const DOC_TYPES = [
  'Passport', 'Visa', 'Emirates ID', 'Labor Card', 
  'Trade License', 'Establishment Card', 'Immigration Card'
];

export default function ExpiryManagementPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [expiryList, setExpiryList] = useState({ items: [], total: 0 });
  const [calendarData, setCalendarData] = useState(null);
  const [services, setServices] = useState([]);
  
  // Filters
  const [filterDays, setFilterDays] = useState(90);
  const [filterLevel, setFilterLevel] = useState('');
  const [filterDocType, setFilterDocType] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  
  // Calendar
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  
  // Create order dialog
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false);
  const [selectedExpiry, setSelectedExpiry] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Bulk reminder dialog
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkDays, setBulkDays] = useState(30);
  const [bulkSending, setBulkSending] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [dashboardRes, listRes, servicesRes] = await Promise.all([
        getExpiryDashboard(),
        getExpiryList({
          days: filterDays,
          level: filterLevel || undefined,
          doc_type: filterDocType || undefined,
          entity_type: filterEntityType || undefined,
          limit: 100
        }),
        getServices({ limit: 100 })
      ]);
      setDashboard(dashboardRes.data);
      setExpiryList(listRes.data);
      setServices(servicesRes.data.items);
    } catch (error) {
      toast.error('Failed to load expiry data');
    } finally {
      setLoading(false);
    }
  }, [filterDays, filterLevel, filterDocType, filterEntityType]);

  const loadCalendar = useCallback(async () => {
    try {
      const res = await getExpiryCalendar(calendarYear, calendarMonth);
      setCalendarData(res.data);
    } catch (error) {
      console.error(error);
    }
  }, [calendarYear, calendarMonth]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  // Memoize active services list for dialog
  const activeServices = useMemo(() => services.filter(s => s.is_active), [services]);

  const handleCreateOrder = async () => {
    if (!selectedExpiry) return;
    
    setCreating(true);
    try {
      const result = await createOrderFromExpiry({
        entity_type: selectedExpiry.entity_type,
        entity_id: selectedExpiry.entity_id,
        document_type: selectedExpiry.document_type,
        service_id: selectedServiceId || undefined,
        notes: `Created from expiry alert - ${selectedExpiry.days_remaining} days remaining`
      });
      
      toast.success(result.data.message);
      setCreateOrderDialogOpen(false);
      navigate(`/service-orders/${result.data.order.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setCreating(false);
    }
  };

  const handleSendBulkReminders = async () => {
    setBulkSending(true);
    try {
      const result = await sendExpiryReminders({
        days_threshold: bulkDays,
        channels: ['whatsapp', 'email'],
        language: 'en'
      });
      
      toast.success(`Sent: ${result.data.sent}, Failed: ${result.data.failed}`);
      setBulkDialogOpen(false);
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setBulkSending(false);
    }
  };

  const getAlertBadge = (level) => {
    const config = ALERT_LEVELS.find(l => l.value === level) || ALERT_LEVELS[6];
    return (
      <Badge className={cn(config.bgColor, config.textColor, 'border-0')}>
        {level === 'expired' ? 'EXPIRED' : `${config.label.split(' ')[0]}`}
      </Badge>
    );
  };

  const getDaysRemainingText = (days) => {
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Expires today';
    if (days === 1) return '1 day left';
    return `${days} days left`;
  };

  const navigateCalendar = (direction) => {
    if (direction === 'prev') {
      if (calendarMonth === 1) {
        setCalendarYear(calendarYear - 1);
        setCalendarMonth(12);
      } else {
        setCalendarMonth(calendarMonth - 1);
      }
    } else {
      if (calendarMonth === 12) {
        setCalendarYear(calendarYear + 1);
        setCalendarMonth(1);
      } else {
        setCalendarMonth(calendarMonth + 1);
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileWarning className="w-6 h-6 text-primary" />
            Document Expiry Management
          </h1>
          <p className="text-muted-foreground">
            Track and manage expiring documents across all clients and workers
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulkDialogOpen(true)}>
            <Send className="w-4 h-4 mr-2" />
            Send Reminders
          </Button>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {ALERT_LEVELS.map((level) => (
          <Card 
            key={level.value} 
            className={cn(
              "cursor-pointer transition-all hover:ring-2 hover:ring-primary/50",
              filterLevel === level.value && "ring-2 ring-primary"
            )}
            onClick={() => setFilterLevel(filterLevel === level.value ? '' : level.value)}
          >
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div className={cn("w-3 h-3 rounded-full", level.color)}></div>
                <span className="text-2xl font-bold">
                  {dashboard?.by_level?.[level.value] || 0}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1 truncate">
                {level.label.split(' ')[0]}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Expiring</p>
                <p className="text-3xl font-bold">{dashboard?.total_expiring || 0}</p>
                <p className="text-xs text-muted-foreground">within 90 days</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Workers Affected</p>
                <p className="text-3xl font-bold">{dashboard?.workers_affected || 0}</p>
              </div>
              <User className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Clients Affected</p>
                <p className="text-3xl font-bold">{dashboard?.clients_affected || 0}</p>
              </div>
              <Building2 className="w-10 h-10 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Needs Immediate Action</p>
                <p className="text-3xl font-bold text-red-700">
                  {(dashboard?.by_level?.expired || 0) + 
                   (dashboard?.by_level?.emergency || 0) + 
                   (dashboard?.by_level?.critical || 0)}
                </p>
                <p className="text-xs text-red-600">expired + critical</p>
              </div>
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="list" className="w-full">
        <TabsList>
          <TabsTrigger value="list" className="gap-2">
            <FileText className="w-4 h-4" /> Document List
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-2">
            <Calendar className="w-4 h-4" /> Calendar View
          </TabsTrigger>
          <TabsTrigger value="by-type" className="gap-2">
            <FileWarning className="w-4 h-4" /> By Document Type
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle>Expiring Documents</CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <Select value={filterDays.toString()} onValueChange={(v) => setFilterDays(parseInt(v))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Next 7 days</SelectItem>
                      <SelectItem value="15">Next 15 days</SelectItem>
                      <SelectItem value="30">Next 30 days</SelectItem>
                      <SelectItem value="60">Next 60 days</SelectItem>
                      <SelectItem value="90">Next 90 days</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={filterDocType || 'all'} onValueChange={(v) => setFilterDocType(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {DOC_TYPES.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={filterEntityType || 'all'} onValueChange={(v) => setFilterEntityType(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="worker">Workers</SelectItem>
                      <SelectItem value="client">Clients</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {expiryList.items.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                  <p className="text-lg font-medium">No expiring documents found</p>
                  <p className="text-sm">All documents are valid within the selected timeframe</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Document</TableHead>
                      <TableHead>Number</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Days Left</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expiryList.items.map((item) => (
                      <TableRow 
                        key={item.id}
                        className={cn(
                          item.alert_level === 'expired' && 'bg-red-50',
                          item.alert_level === 'emergency' && 'bg-red-50/50',
                          item.alert_level === 'critical' && 'bg-orange-50'
                        )}
                      >
                        <TableCell>{getAlertBadge(item.alert_level)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {item.entity_type === 'worker' ? (
                              <User className="w-4 h-4 text-blue-500" />
                            ) : (
                              <Building2 className="w-4 h-4 text-purple-500" />
                            )}
                            <div>
                              <p className="font-medium">{item.entity_name}</p>
                              <p className="text-xs text-muted-foreground">{item.entity_code}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{item.client_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.document_type}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{item.document_number}</TableCell>
                        <TableCell>{formatDate(item.expiry_date)}</TableCell>
                        <TableCell>
                          <span className={cn(
                            "font-medium",
                            item.days_remaining <= 0 && "text-red-600",
                            item.days_remaining > 0 && item.days_remaining <= 7 && "text-orange-600",
                            item.days_remaining > 7 && item.days_remaining <= 15 && "text-amber-600"
                          )}>
                            {getDaysRemainingText(item.days_remaining)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedExpiry(item);
                              setSelectedServiceId('');
                              setCreateOrderDialogOpen(true);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Create Order
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendar" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Expiry Calendar</CardTitle>
                <div className="flex items-center gap-4">
                  <Button variant="outline" size="icon" onClick={() => navigateCalendar('prev')}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="font-medium min-w-[150px] text-center">
                    {monthNames[calendarMonth - 1]} {calendarYear}
                  </span>
                  <Button variant="outline" size="icon" onClick={() => navigateCalendar('next')}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {calendarData && (
                <div className="grid grid-cols-7 gap-1">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                      {day}
                    </div>
                  ))}
                  
                  {/* Empty cells for days before month start */}
                  {Array.from({ length: new Date(calendarYear, calendarMonth - 1, 1).getDay() }).map((_, i) => (
                    <div key={`empty-${i}`} className="h-24 border rounded-lg bg-muted/30"></div>
                  ))}
                  
                  {calendarData.days.map((day) => {
                    const dayNum = parseInt(day.date.split('-')[2]);
                    const isToday = day.date === new Date().toISOString().split('T')[0];
                    
                    return (
                      <div
                        key={day.date}
                        className={cn(
                          "h-24 border rounded-lg p-2 overflow-hidden",
                          isToday && "ring-2 ring-primary",
                          day.has_critical && "bg-red-50 border-red-200",
                          !day.has_critical && day.has_urgent && "bg-amber-50 border-amber-200",
                          day.count > 0 && !day.has_critical && !day.has_urgent && "bg-yellow-50 border-yellow-200"
                        )}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={cn(
                            "text-sm font-medium",
                            isToday && "text-primary"
                          )}>
                            {dayNum}
                          </span>
                          {day.count > 0 && (
                            <Badge 
                              variant={day.has_critical ? "destructive" : "secondary"} 
                              className="text-xs h-5"
                            >
                              {day.count}
                            </Badge>
                          )}
                        </div>
                        <div className="space-y-0.5 overflow-hidden">
                          {day.items.slice(0, 2).map((item, idx) => (
                            <div 
                              key={idx} 
                              className="text-xs truncate text-muted-foreground"
                              title={`${item.entity_name} - ${item.document_type}`}
                            >
                              {item.document_type}
                            </div>
                          ))}
                          {day.items.length > 2 && (
                            <div className="text-xs text-muted-foreground">
                              +{day.items.length - 2} more
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-type" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(dashboard?.by_type || {}).map(([type, count]) => (
              <Card 
                key={type}
                className={cn(
                  "cursor-pointer transition-all hover:ring-2 hover:ring-primary/50",
                  filterDocType === type && "ring-2 ring-primary"
                )}
                onClick={() => {
                  setFilterDocType(type);
                  // Switch to list tab
                  const listTab = document.querySelector('[data-state="inactive"][value="list"]');
                  if (listTab) listTab.click();
                }}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Expiring</p>
                      <p className="text-2xl font-bold">{count}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-sm">{type}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {Object.keys(dashboard?.by_type || {}).length === 0 && (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                <p>No expiring documents by type</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Order Dialog */}
      <Dialog open={createOrderDialogOpen} onOpenChange={setCreateOrderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Service Order</DialogTitle>
            <DialogDescription>
              Create a renewal order for this expiring document
            </DialogDescription>
          </DialogHeader>
          
          {selectedExpiry && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Entity:</span>
                  <span className="font-medium">{selectedExpiry.entity_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Document:</span>
                  <span className="font-medium">{selectedExpiry.document_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expiry:</span>
                  <span className={cn(
                    "font-medium",
                    selectedExpiry.days_remaining <= 7 && "text-red-600"
                  )}>
                    {formatDate(selectedExpiry.expiry_date)} ({getDaysRemainingText(selectedExpiry.days_remaining)})
                  </span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Service (Optional)</label>
                <Select value={selectedServiceId} onValueChange={setSelectedServiceId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Auto-select based on document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeServices.map(service => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Leave empty to auto-select a matching service
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOrderDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateOrder} disabled={creating}>
              {creating ? 'Creating...' : 'Create Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Reminder Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Expiry Reminders</DialogTitle>
            <DialogDescription>
              Send notifications to all clients with expiring documents
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Days Threshold</label>
              <Select value={bulkDays.toString()} onValueChange={(v) => setBulkDays(parseInt(v))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Documents expiring in 7 days</SelectItem>
                  <SelectItem value="15">Documents expiring in 15 days</SelectItem>
                  <SelectItem value="30">Documents expiring in 30 days</SelectItem>
                  <SelectItem value="60">Documents expiring in 60 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm">
                This will send WhatsApp and Email reminders to all clients with documents 
                expiring within <strong>{bulkDays} days</strong>.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendBulkReminders} disabled={bulkSending}>
              {bulkSending ? 'Sending...' : 'Send Reminders'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
